import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useTheme } from "../../../context/ThemeContext";

interface Product {
  id: string;
  name: string;
  price: number;
  category: string;
  image?: string;
  sku: string;
}

interface CartItem extends Product {
  quantity: number;
  discount?: number;
  note?: string;
}

const MOCK_PRODUCTS: Product[] = [
  { id: "1", name: "Product A", price: 29.99, category: "Electronics", sku: "ELEC-001" },
  { id: "2", name: "Product B", price: 15.50, category: "Clothing", sku: "CLTH-002" },
  { id: "3", name: "Product C", price: 45.00, category: "Home", sku: "HOME-003" },
  { id: "4", name: "Product D", price: 8.99, category: "Food", sku: "FOOD-004" },
  { id: "5", name: "Product E", price: 120.00, category: "Electronics", sku: "ELEC-005" },
  { id: "6", name: "Product F", price: 35.75, category: "Clothing", sku: "CLTH-006" },
];

const CATEGORIES = ["All", "Electronics", "Clothing", "Home", "Food"];

export default function TerminalSection() {
  const { t } = useTranslation("common");
  const { isDarkMode } = useTheme();
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedCategory, setSelectedCategory] = useState("All");
  const [cart, setCart] = useState<CartItem[]>([]);

  const themeClasses = {
    bg: isDarkMode ? "bg-dark-surface" : "bg-white",
    border: isDarkMode ? "border-dark-border" : "border-gray-200",
    text: isDarkMode ? "text-white" : "text-gray-900",
    textSecondary: isDarkMode ? "text-gray-400" : "text-gray-600",
    cardBg: isDarkMode ? "bg-dark-card" : "bg-gray-50",
    inputBg: isDarkMode ? "bg-dark-surface" : "bg-white",
  };

  const filteredProducts = MOCK_PRODUCTS.filter((product) => {
    const matchesSearch = product.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      product.sku.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesCategory = selectedCategory === "All" || product.category === selectedCategory;
    return matchesSearch && matchesCategory;
  });

  const addToCart = (product: Product) => {
    setCart((prev) => {
      const existing = prev.find((item) => item.id === product.id);
      if (existing) {
        return prev.map((item) =>
          item.id === product.id ? { ...item, quantity: item.quantity + 1 } : item
        );
      }
      return [...prev, { ...product, quantity: 1 }];
    });
  };

  const updateQuantity = (productId: string, delta: number) => {
    setCart((prev) =>
      prev
        .map((item) =>
          item.id === productId ? { ...item, quantity: Math.max(0, item.quantity + delta) } : item
        )
        .filter((item) => item.quantity > 0)
    );
  };

  const removeFromCart = (productId: string) => {
    setCart((prev) => prev.filter((item) => item.id !== productId));
  };

  const subtotal = cart.reduce((acc, item) => acc + item.price * item.quantity, 0);
  const tax = subtotal * 0.16;
  const total = subtotal + tax;

  return (
    <div className="h-full flex">
      <div className="flex-1 p-4 sm:p-6 overflow-y-auto">
        <div className="mb-6">
          <div className="flex flex-col sm:flex-row gap-4 mb-4">
            <div className="flex-1 relative">
              <i className={`ri-search-line absolute left-3 top-1/2 -translate-y-1/2 ${themeClasses.textSecondary}`}></i>
              <input
                type="text"
                placeholder={t("posTerminal.searchProducts")}
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className={`w-full pl-10 pr-4 py-3 rounded-xl border ${themeClasses.border} ${themeClasses.inputBg} ${themeClasses.text} focus:outline-none focus:ring-2 focus:ring-primary-500`}
              />
            </div>
            <button className="px-4 py-3 rounded-xl bg-primary-500 text-gray-900 font-medium flex items-center justify-center gap-2">
              <i className="ri-barcode-line text-lg"></i>
              {t("posTerminal.scanBarcode")}
            </button>
          </div>

          <div className="flex gap-2 overflow-x-auto pb-2">
            {CATEGORIES.map((category) => (
              <button
                key={category}
                onClick={() => setSelectedCategory(category)}
                className={`px-4 py-2 rounded-lg whitespace-nowrap transition-all ${
                  selectedCategory === category
                    ? "bg-primary-500 text-gray-900 font-medium"
                    : `${themeClasses.cardBg} ${themeClasses.text} border ${themeClasses.border}`
                }`}
              >
                {category}
              </button>
            ))}
          </div>
        </div>

        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-4">
          {filteredProducts.map((product) => (
            <button
              key={product.id}
              onClick={() => addToCart(product)}
              className={`${themeClasses.cardBg} border ${themeClasses.border} rounded-xl p-4 text-left hover:border-primary-500 transition-all group`}
            >
              <div className="aspect-square rounded-lg bg-gradient-to-br from-gray-200 to-gray-300 dark:from-gray-600 dark:to-gray-700 mb-3 flex items-center justify-center">
                <i className={`ri-box-3-line text-3xl ${themeClasses.textSecondary}`}></i>
              </div>
              <p className={`font-medium ${themeClasses.text} truncate`}>{product.name}</p>
              <p className={`text-xs ${themeClasses.textSecondary}`}>{product.sku}</p>
              <p className="text-primary-500 font-bold mt-1">${product.price.toFixed(2)}</p>
            </button>
          ))}
        </div>
      </div>

      <div className={`w-80 lg:w-96 ${themeClasses.bg} border-l ${themeClasses.border} flex flex-col`}>
        <div className={`p-4 border-b ${themeClasses.border}`}>
          <h2 className={`text-lg font-bold ${themeClasses.text}`}>{t("posTerminal.currentSale")}</h2>
          <p className={`text-sm ${themeClasses.textSecondary}`}>
            {cart.length} {t("posTerminal.items")}
          </p>
        </div>

        <div className="flex-1 overflow-y-auto p-4 space-y-3">
          {cart.length === 0 ? (
            <div className="flex flex-col items-center justify-center h-full text-center">
              <i className={`ri-shopping-cart-line text-4xl ${themeClasses.textSecondary} mb-2`}></i>
              <p className={themeClasses.textSecondary}>{t("posTerminal.emptyCart")}</p>
            </div>
          ) : (
            cart.map((item) => (
              <div
                key={item.id}
                className={`${themeClasses.cardBg} rounded-xl p-3 border ${themeClasses.border}`}
              >
                <div className="flex justify-between items-start mb-2">
                  <div className="flex-1">
                    <p className={`font-medium ${themeClasses.text}`}>{item.name}</p>
                    <p className={`text-sm ${themeClasses.textSecondary}`}>
                      ${item.price.toFixed(2)} × {item.quantity}
                    </p>
                  </div>
                  <button
                    onClick={() => removeFromCart(item.id)}
                    className="text-red-500 hover:text-red-600 p-1"
                  >
                    <i className="ri-delete-bin-line"></i>
                  </button>
                </div>
                <div className="flex items-center gap-2">
                  <button
                    onClick={() => updateQuantity(item.id, -1)}
                    className={`w-8 h-8 rounded-lg ${themeClasses.bg} border ${themeClasses.border} flex items-center justify-center`}
                  >
                    <i className={`ri-subtract-line ${themeClasses.text}`}></i>
                  </button>
                  <span className={`font-medium ${themeClasses.text} min-w-[2rem] text-center`}>
                    {item.quantity}
                  </span>
                  <button
                    onClick={() => updateQuantity(item.id, 1)}
                    className="w-8 h-8 rounded-lg bg-primary-500 text-gray-900 flex items-center justify-center"
                  >
                    <i className="ri-add-line"></i>
                  </button>
                  <span className={`ml-auto font-bold ${themeClasses.text}`}>
                    ${(item.price * item.quantity).toFixed(2)}
                  </span>
                </div>
              </div>
            ))
          )}
        </div>

        <div className={`p-4 border-t ${themeClasses.border} space-y-3`}>
          <div className="flex justify-between">
            <span className={themeClasses.textSecondary}>{t("posTerminal.subtotal")}</span>
            <span className={themeClasses.text}>${subtotal.toFixed(2)}</span>
          </div>
          <div className="flex justify-between">
            <span className={themeClasses.textSecondary}>{t("posTerminal.tax")} (16%)</span>
            <span className={themeClasses.text}>${tax.toFixed(2)}</span>
          </div>
          <div className={`flex justify-between text-lg font-bold border-t ${themeClasses.border} pt-3`}>
            <span className={themeClasses.text}>{t("posTerminal.total")}</span>
            <span className="text-primary-500">${total.toFixed(2)}</span>
          </div>

          <div className="grid grid-cols-2 gap-2">
            <button className={`p-3 rounded-xl border ${themeClasses.border} ${themeClasses.text} font-medium`}>
              <i className="ri-pause-line mr-2"></i>
              {t("posTerminal.hold")}
            </button>
            <button className={`p-3 rounded-xl border ${themeClasses.border} ${themeClasses.text} font-medium`}>
              <i className="ri-percent-line mr-2"></i>
              {t("posTerminal.discount")}
            </button>
          </div>

          <button
            disabled={cart.length === 0}
            className="w-full p-4 rounded-xl bg-primary-500 text-gray-900 font-bold text-lg disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <i className="ri-wallet-3-line mr-2"></i>
            {t("posTerminal.checkout")}
          </button>
        </div>
      </div>
    </div>
  );
}
